// src/pages/CollaboratorPortal.jsx
// Full portal: overview, post opportunity (→ admin review), listings with status, comment threads
import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import { db }   from '../config/firebase';
import { useAuth } from '../hooks/useAuth';
import { submitOpportunity, addCollaboratorComment, markCommentsRead } from '../services/opportunityService';
import { updateApplicationStatus } from '../services/applicationService';
import Footer from '../components/layout/Footer';
import { C }  from '../styles/tokens';

/* ── helpers ─────────────────────────────────────────────────────── */
const STATUS_META = {
  pending:   { label: 'Pending Review', color: C.warning,  icon: '🕐', bg: 'rgba(245,158,11,0.12)' },
  live:      { label: 'Live',           color: C.success,  icon: '✅', bg: 'rgba(0,208,132,0.12)'  },
  rejected:  { label: 'Rejected',       color: C.danger,   icon: '❌', bg: 'rgba(239,68,68,0.12)'  },
  suspended: { label: 'Suspended',      color: C.warning,  icon: '⚠️', bg: 'rgba(245,158,11,0.12)' },
};

const field = (label, node) => (
  <div>
    <label style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.45)', display: 'block', marginBottom: 7, letterSpacing: '.06em', textTransform: 'uppercase' }}>{label}</label>
    {node}
  </div>
);

const inputStyle = { width: '100%', padding: '13px 14px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(254,52,232,0.2)', borderRadius: 9, color: '#fff', fontSize: 14, outline: 'none', fontFamily: "'DM Sans',sans-serif" };

/* ── Comment Thread Panel ────────────────────────────────────────── */
function CommentPanel({ job, user, profile, onClose }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const comments = job.comments || [];

  useEffect(() => {
    if (job.hasUnreadComments) markCommentsRead(job.id).catch(() => {});
  }, [job.id, job.hasUnreadComments]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await addCollaboratorComment(job.id, user.uid, profile?.displayName || 'Collaborator', text.trim());
      setText('');
      toast.success('Reply sent!');
    } catch (e) {
      toast.error(e.message);
    } finally { setSending(false); }
  };

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(4,4,15,0.88)', backdropFilter: 'blur(14px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 560, background: C.surface2, border: '1px solid rgba(254,52,232,0.2)', borderRadius: 20, overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '85vh', animation: 'fadeUp .3s ease' }}>
        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginBottom: 3 }}>Comments on</div>
            <h3 className="syne" style={{ fontSize: 17, fontWeight: 800 }}>{job.title}</h3>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.07)', border: 'none', color: 'white', width: 30, height: 30, borderRadius: 7, cursor: 'pointer', fontSize: 14, flexShrink: 0 }}>✕</button>
        </div>

        {/* Thread */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {comments.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'rgba(255,255,255,0.3)' }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>💬</div>
              <p style={{ fontSize: 13 }}>No comments yet. Admin comments will appear here.</p>
            </div>
          )}
          {comments.map((c) => {
            const isAdmin = c.role === 'admin';
            return (
              <div key={c.id} style={{ display: 'flex', gap: 10, flexDirection: isAdmin ? 'row' : 'row-reverse' }}>
                <div style={{ width: 34, height: 34, borderRadius: '50%', background: isAdmin ? `linear-gradient(135deg,${C.magenta},${C.violet})` : `linear-gradient(135deg,${C.info},${C.violet})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0 }}>
                  {c.authorName?.charAt(0) || '?'}
                </div>
                <div style={{ maxWidth: '75%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexDirection: isAdmin ? 'row' : 'row-reverse' }}>
                    <span style={{ fontSize: 12, fontWeight: 700 }}>{c.authorName}</span>
                    <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 100, background: isAdmin ? 'rgba(254,52,232,0.15)' : 'rgba(59,130,246,0.15)', color: isAdmin ? C.magenta : C.info, fontWeight: 700 }}>
                      {isAdmin ? 'Admin' : 'You'}
                    </span>
                    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>{new Date(c.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div style={{ padding: '10px 14px', borderRadius: isAdmin ? '4px 12px 12px 12px' : '12px 4px 12px 12px', background: isAdmin ? 'rgba(254,52,232,0.08)' : 'rgba(59,130,246,0.08)', border: `1px solid ${isAdmin ? 'rgba(254,52,232,0.15)' : 'rgba(59,130,246,0.15)'}`, fontSize: 13.5, lineHeight: 1.6, color: 'rgba(255,255,255,0.85)' }}>
                    {c.text}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Reply box */}
        <div style={{ padding: '14px 22px 20px', borderTop: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(); }}
              placeholder="Reply to admin comment… (Ctrl+Enter to send)"
              rows={2}
              style={{ flex: 1, ...inputStyle, resize: 'none', fontSize: 13 }}
            />
            <button
              onClick={send}
              disabled={!text.trim() || sending}
              style={{ padding: '0 18px', background: text.trim() ? `linear-gradient(135deg,${C.magenta},${C.violet})` : 'rgba(255,255,255,0.05)', border: 'none', borderRadius: 9, color: '#fff', fontWeight: 700, cursor: text.trim() ? 'pointer' : 'default', fontSize: 13, flexShrink: 0, fontFamily: "'Syne',sans-serif", opacity: sending ? 0.7 : 1 }}
            >
              {sending ? '…' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Status Banner (shown in listing row) ────────────────────────── */
function StatusBanner({ job, onViewComments }) {
  const meta = STATUS_META[job.status] || STATUS_META.pending;
  if (job.status === 'live' && !job.comments?.length) return null;

  return (
    <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 9, background: meta.bg, border: `1px solid ${meta.color}25`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
      <div style={{ fontSize: 12.5, color: meta.color, lineHeight: 1.5, flex: 1 }}>
        {job.status === 'rejected'  && <><strong>Not Approved.</strong> {job.comments?.length ? ' Admin left feedback — review it and resubmit if you wish.' : ' No specific reason given. Contact support for more info.'}</>}
        {job.status === 'suspended' && <><strong>Temporarily Suspended.</strong> Admin may have flagged a policy issue. Review comments below.</>}
        {job.status === 'pending'   && <><strong>Under Review.</strong> Typically approved within 2–4 hours.</>}
        {job.status === 'live'      && job.comments?.length > 0 && <><strong>Admin note:</strong> You have comments from admin on this listing.</>}
      </div>
      {job.comments?.length > 0 && (
        <button onClick={onViewComments} style={{ padding: '5px 14px', background: `${meta.color}15`, border: `1px solid ${meta.color}35`, borderRadius: 7, color: meta.color, fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5 }}>
          💬 View Comments {job.hasUnreadComments && <span style={{ width: 7, height: 7, borderRadius: '50%', background: meta.color, display: 'inline-block' }} />}
        </button>
      )}
    </div>
  );
}

/* ── Main Component ──────────────────────────────────────────────── */
export default function CollaboratorPortal() {
  const { user, profile } = useAuth();
  const [view,         setView]         = useState('overview');
  const [jobs,         setJobs]         = useState([]);
  const [loadingJobs,  setLoadingJobs]  = useState(true);
  const [applications, setApplications] = useState([]);
  const [loadingApps,  setLoadingApps]  = useState(true);
  const [appFilter,    setAppFilter]    = useState('all');
  const [selectedJob,  setSelectedJob]  = useState('all'); // filter apps by job
  const [commentJob,   setCommentJob]   = useState(null); // job whose thread is open
  const [submitting,   setSubmitting]   = useState(false);
  const [jobFilter,    setJobFilter]    = useState('all');

  // Form state
  const [form, setForm] = useState({ title: '', company: '', location: '', payMin: '', payMax: '', category: 'Engineering', type: 'Full-time', description: '', skills: '' });
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  /* Live-subscribe to this collaborator's opportunities */
  useEffect(() => {
    if (!user) return;
    setLoadingJobs(true);
    const q = query(collection(db, 'opportunities'), where('collaboratorId', '==', user.uid));
    const unsub = onSnapshot(q, snap => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
      setJobs(docs);
      setLoadingJobs(false);
    });
    return () => unsub();
  }, [user]);

  /* Live-subscribe to applications for this collaborator's jobs */
  useEffect(() => {
    if (!user) return;
    setLoadingApps(true);
    const q = query(collection(db, 'applications'), where('collaboratorId', '==', user.uid));
    const unsub = onSnapshot(q, snap => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
      setApplications(docs);
      setLoadingApps(false);
    });
    return () => unsub();
  }, [user]);

  /* Submit new opportunity */
  const handleSubmit = async () => {
    if (!form.title.trim() || !form.description.trim()) { toast.error('Title and description are required'); return; }
    setSubmitting(true);
    try {
      const skillsArr = form.skills.split(',').map(s => s.trim()).filter(Boolean);
      await submitOpportunity(user.uid, profile?.displayName || 'Collaborator', {
        ...form,
        skills:    skillsArr,
        payMin:    parseInt(form.payMin) || 0,
        payMax:    parseInt(form.payMax) || 0,
        payDisplay: form.payMin && form.payMax ? `${form.payMin}–${form.payMax} ONT/mo` : 'Negotiable',
        applicants: 0,
      });
      toast.success('Opportunity submitted for admin review! 🎉');
      setForm({ title: '', company: '', location: '', payMin: '', payMax: '', category: 'Engineering', type: 'Full-time', description: '', skills: '' });
      setView('listings');
    } catch (e) {
      toast.error(e.message);
    } finally { setSubmitting(false); }
  };

  const liveJobs    = jobs.filter(j => j.status === 'live');
  const pendingJobs = jobs.filter(j => j.status === 'pending');
  const rejectedJobs= jobs.filter(j => j.status === 'rejected');
  const filteredJobs= jobFilter === 'all' ? jobs : jobs.filter(j => j.status === jobFilter);
  const unreadCount = jobs.filter(j => j.hasUnreadComments).length;

  const TABS = [
    { k: 'overview',  l: '📊 Overview' },
    { k: 'post',      l: '➕ Post Opportunity' },
    { k: 'listings',  l: `📋 My Listings${unreadCount ? ` 🔴` : ''}` },
    { k: 'applicants',l: '👥 Applicants' },
  ];

  return (
    <div style={{ background: C.bg, paddingTop: 64 }}>
      <style>{`
        .cp-grid4 { display: grid; grid-template-columns: repeat(4,1fr); gap: 14px; margin-bottom: 24px; }
        .cp-form2  { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        @media (max-width: 900px) { .cp-grid4 { grid-template-columns: 1fr 1fr !important; } }
        @media (max-width: 600px) {
          .cp-grid4 { grid-template-columns: 1fr 1fr !important; }
          .cp-form2 { grid-template-columns: 1fr !important; }
          .cp-tabs  { overflow-x: auto; flex-wrap: nowrap !important; padding-bottom: 4px; }
        }
      `}</style>

      {/* Header */}
      <div style={{ background: `linear-gradient(180deg,${C.navy}60,transparent)`, padding: 'clamp(36px,6vw,52px) 0 clamp(24px,4vw,36px)', position: 'relative', overflow: 'hidden' }}>
        <div className="grid-bg" style={{ opacity: .4 }} />
        <div className="container" style={{ position: 'relative', zIndex: 1, padding: '0 clamp(16px,4vw,28px)' }}>
          <span className="pill" style={{ background: 'rgba(8,12,239,.15)', border: '1px solid rgba(8,12,239,.3)', color: '#818cf8', marginBottom: 16, display: 'inline-flex' }}>🏢 Collaborator Portal</span>
          <h1 className="syne" style={{ fontSize: 'clamp(1.8rem,4vw,3rem)', fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 10 }}>
            Industry <span className="grad-text">Partner Dashboard</span>
          </h1>
          <p style={{ color: 'rgba(255,255,255,.48)', fontSize: 15, marginBottom: 26 }}>
            Post opportunities, review AI-matched talent, and pay instantly via ONT Tokens.
          </p>
          <div className="cp-tabs" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {TABS.map(({ k, l }) => (
              <button key={k} onClick={() => setView(k)} style={{ padding: '10px 22px', borderRadius: 9, background: view === k ? `linear-gradient(135deg,${C.magenta},${C.violet})` : 'rgba(255,255,255,.06)', border: view === k ? 'none' : '1px solid rgba(255,255,255,.1)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'all .3s', fontFamily: "'Syne',sans-serif", boxShadow: view === k ? `0 6px 20px rgba(254,52,232,.3)` : 'none', whiteSpace: 'nowrap' }}>{l}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="container" style={{ padding: 'clamp(20px,3vw,28px) clamp(16px,4vw,28px) 60px' }}>

        {/* ── OVERVIEW ── */}
        {view === 'overview' && (
          <div>
            <div className="cp-grid4">
              {[
                ['📋', 'Active Listings', liveJobs.length,       C.success],
                ['🕐', 'Pending Review',  pendingJobs.length,    C.warning],
                ['❌', 'Rejected',        rejectedJobs.length,   C.danger],
                ['👥', 'Total Applicants',applications.length,   C.violet],
              ].map(([ic, lb, val, col]) => (
                <div key={lb} className="card" style={{ padding: 20 }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,${col}70,transparent)` }} />
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: `${col}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19, marginBottom: 12 }}>{ic}</div>
                  <div className="syne" style={{ fontSize: 28, fontWeight: 800, color: col }}>{val}</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,.5)', fontWeight: 600, marginTop: 3 }}>{lb}</div>
                </div>
              ))}
            </div>

            {/* Pending/rejected alerts */}
            {pendingJobs.length > 0 && (
              <div style={{ padding: '14px 18px', borderRadius: 12, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 20 }}>🕐</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.warning }}>{pendingJobs.length} listing{pendingJobs.length > 1 ? 's' : ''} awaiting admin review</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>Usually reviewed within 2–4 hours.</div>
                </div>
                <button onClick={() => setView('listings')} style={{ marginLeft: 'auto', padding: '7px 14px', background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, color: C.warning, fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>View →</button>
              </div>
            )}
            {rejectedJobs.length > 0 && (
              <div style={{ padding: '14px 18px', borderRadius: 12, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 20 }}>❌</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.danger }}>{rejectedJobs.length} listing{rejectedJobs.length > 1 ? 's' : ''} rejected</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>Check admin comments and resubmit if resolved.</div>
                </div>
                <button onClick={() => setView('listings')} style={{ marginLeft: 'auto', padding: '7px 14px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: C.danger, fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>Review →</button>
              </div>
            )}

            {/* Listings preview */}
            <div className="card" style={{ padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
                <h3 className="syne" style={{ fontSize: 16, fontWeight: 700 }}>Your Listings</h3>
                <button onClick={() => setView('post')} style={{ padding: '9px 18px', background: `linear-gradient(135deg,${C.magenta},${C.violet})`, border: 'none', borderRadius: 9, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Syne',sans-serif" }}>+ Post New</button>
              </div>
              {loadingJobs ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.3)' }}>Loading…</div>
              ) : jobs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>📭</div>
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>No opportunities posted yet.</p>
                  <button onClick={() => setView('post')} style={{ marginTop: 14, padding: '10px 22px', background: `linear-gradient(135deg,${C.magenta},${C.violet})`, border: 'none', borderRadius: 9, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Syne',sans-serif" }}>Post Your First Opportunity →</button>
                </div>
              ) : jobs.slice(0, 5).map((j) => {
                const meta = STATUS_META[j.status] || STATUS_META.pending;
                return (
                  <div key={j.id} style={{ padding: '14px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span className="syne" style={{ fontSize: 14, fontWeight: 700 }}>{j.title}</span>
                          {j.featured && <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 100, background: 'rgba(254,52,232,0.15)', color: C.magenta, fontWeight: 700 }}>⭐ Featured</span>}
                          {j.urgent   && <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 100, background: 'rgba(239,68,68,0.15)', color: C.danger, fontWeight: 700 }}>🔥 Urgent</span>}
                        </div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{j.location} · {j.applicants || 0} applicants</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 100, background: meta.bg, color: meta.color, fontWeight: 700 }}>{meta.icon} {meta.label}</span>
                        {j.comments?.length > 0 && (
                          <button onClick={() => setCommentJob(j)} style={{ padding: '5px 12px', background: j.hasUnreadComments ? 'rgba(254,52,232,0.15)' : 'rgba(255,255,255,0.06)', border: `1px solid ${j.hasUnreadComments ? 'rgba(254,52,232,0.3)' : 'rgba(255,255,255,0.1)'}`, borderRadius: 7, color: j.hasUnreadComments ? C.magenta : 'rgba(255,255,255,0.6)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                            💬 {j.comments.length} {j.hasUnreadComments ? '🔴' : ''}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── POST OPPORTUNITY ── */}
        {view === 'post' && (
          <div style={{ maxWidth: 720 }}>
            <div className="card" style={{ padding: 'clamp(20px,4vw,32px)' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${C.magenta},${C.violet})` }} />
              <h2 className="syne" style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>Post New Opportunity</h2>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,.4)', marginBottom: 26 }}>Reviewed by admin before going live — typically approved within 2–4 hours.</p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="cp-form2">
                  {field('Job Title',    <input value={form.title}    onChange={e => setF('title', e.target.value)}   placeholder="e.g. Senior Product Designer" style={inputStyle} />)}
                  {field('Company Name', <input value={form.company}  onChange={e => setF('company', e.target.value)} placeholder="Your company" style={inputStyle} />)}
                </div>
                <div className="cp-form2">
                  {field('Location', <input value={form.location} onChange={e => setF('location', e.target.value)} placeholder="e.g. Remote · Global" style={inputStyle} />)}
                  {field('Job Type',  (
                    <select value={form.type} onChange={e => setF('type', e.target.value)} style={inputStyle}>
                      {['Full-time','Part-time','Contract','Freelance','Internship'].map(t => <option key={t}>{t}</option>)}
                    </select>
                  ))}
                </div>
                <div className="cp-form2">
                  {field('Min Pay (ONT/mo)', <input type="number" value={form.payMin} onChange={e => setF('payMin', e.target.value)} placeholder="e.g. 150" style={inputStyle} />)}
                  {field('Max Pay (ONT/mo)', <input type="number" value={form.payMax} onChange={e => setF('payMax', e.target.value)} placeholder="e.g. 250" style={inputStyle} />)}
                </div>
                {field('Category', (
                  <select value={form.category} onChange={e => setF('category', e.target.value)} style={inputStyle}>
                    {['Design & Creative','Engineering','Marketing','Product','Operations','Finance','Data & AI','Customer Success'].map(c => <option key={c}>{c}</option>)}
                  </select>
                ))}
                {field('Job Description', (
                  <textarea value={form.description} onChange={e => setF('description', e.target.value)} placeholder="Describe the role, responsibilities, and ideal candidate…" rows={6} style={{ ...inputStyle, resize: 'vertical' }} />
                ))}
                {field('Required Skills (comma separated)', (
                  <input value={form.skills} onChange={e => setF('skills', e.target.value)} placeholder="e.g. React, Figma, TypeScript, Node.js" style={inputStyle} />
                ))}

                <div style={{ padding: 16, borderRadius: 10, background: 'rgba(8,12,239,.1)', border: '1px solid rgba(8,12,239,.2)' }}>
                  <p style={{ fontSize: 13, color: 'rgba(255,255,255,.6)', lineHeight: 1.6 }}>💡 <strong style={{ color: 'white' }}>Review Process:</strong> Your listing goes to admin for content review. You'll be notified when it's approved or if changes are needed. Approved listings go live instantly on the talent job board.</p>
                </div>

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <button onClick={handleSubmit} disabled={submitting} style={{ flex: 1, minWidth: 180, padding: '13px', background: submitting ? 'rgba(255,255,255,0.1)' : `linear-gradient(135deg,${C.magenta},${C.violet})`, border: 'none', borderRadius: 9, color: '#fff', fontSize: 14, fontWeight: 700, cursor: submitting ? 'default' : 'pointer', fontFamily: "'Syne',sans-serif", opacity: submitting ? 0.7 : 1 }}>
                    {submitting ? 'Submitting…' : 'Submit for Review →'}
                  </button>
                  <button onClick={() => setForm({ title: '', company: '', location: '', payMin: '', payMax: '', category: 'Engineering', type: 'Full-time', description: '', skills: '' })} style={{ padding: '13px 20px', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 9, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Clear</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── LISTINGS ── */}
        {view === 'listings' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
              <h2 className="syne" style={{ fontSize: 20, fontWeight: 800 }}>My Listings</h2>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {['all','live','pending','rejected','suspended'].map(f => (
                  <button key={f} onClick={() => setJobFilter(f)} style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', background: jobFilter === f ? `linear-gradient(135deg,${C.magenta},${C.violet})` : 'rgba(255,255,255,0.05)', border: jobFilter === f ? 'none' : '1px solid rgba(255,255,255,0.1)', color: '#fff', textTransform: 'capitalize' }}>{f === 'all' ? `All (${jobs.length})` : `${STATUS_META[f]?.icon} ${f} (${jobs.filter(j=>j.status===f).length})`}</button>
                ))}
              </div>
            </div>

            {loadingJobs ? (
              <div style={{ textAlign: 'center', padding: 60, color: 'rgba(255,255,255,0.3)' }}>Loading…</div>
            ) : filteredJobs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60, color: 'rgba(255,255,255,0.3)' }}>No listings match this filter.</div>
            ) : filteredJobs.map(j => {
              const meta = STATUS_META[j.status] || STATUS_META.pending;
              return (
                <div key={j.id} className="card" style={{ padding: 'clamp(16px,3vw,24px)', marginBottom: 14 }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,${meta.color}70,transparent)` }} />
                  <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                        <h3 className="syne" style={{ fontSize: 16, fontWeight: 800 }}>{j.title}</h3>
                        <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 100, background: meta.bg, color: meta.color, fontWeight: 700 }}>{meta.icon} {meta.label}</span>
                        {j.featured && <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 100, background: 'rgba(254,52,232,0.15)', color: C.magenta, fontWeight: 700 }}>⭐ Featured</span>}
                        {j.urgent   && <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 100, background: 'rgba(239,68,68,0.15)',  color: C.danger,  fontWeight: 700 }}>🔥 Urgent</span>}
                      </div>
                      <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>
                        {j.company} · {j.location} · {j.type} · {j.category}
                      </div>
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>
                        Posted {j.createdAt?.toDate?.().toLocaleDateString() || 'Recently'} · {j.applicants || 0} applicants
                        {j.moderatedBy && ` · Reviewed by ${j.moderatedBy}`}
                      </div>
                      <StatusBanner job={j} onViewComments={() => setCommentJob(j)} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end', flexShrink: 0 }}>
                      <div className="syne" style={{ fontSize: 15, fontWeight: 800, color: C.success }}>{j.payDisplay || 'Negotiable'}</div>
                      <button onClick={() => setCommentJob(j)} style={{ padding: '8px 14px', background: j.hasUnreadComments ? 'rgba(254,52,232,0.12)' : 'rgba(255,255,255,0.06)', border: `1px solid ${j.hasUnreadComments ? 'rgba(254,52,232,0.3)' : 'rgba(255,255,255,0.1)'}`, borderRadius: 8, color: j.hasUnreadComments ? C.magenta : 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                        💬 {j.comments?.length || 0} comment{j.comments?.length !== 1 ? 's' : ''} {j.hasUnreadComments ? '🔴' : ''}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── APPLICANTS ── */}
        {view === 'applicants' && (() => {
          const STATUS_COLORS = { new: C.info, shortlisted: C.success, rejected: C.danger, hired: C.magenta };
          const STATUS_LABELS = { new: '🆕 New', shortlisted: '⭐ Shortlisted', rejected: '❌ Rejected', hired: '🎉 Hired' };

          const filteredApps = applications.filter(a => {
            const matchJob    = selectedJob === 'all' || a.opportunityId === selectedJob;
            const matchStatus = appFilter   === 'all' || a.status === appFilter;
            return matchJob && matchStatus;
          });

          return (
            <div>
              {/* Filters */}
              <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:20, alignItems:'center', justifyContent:'space-between' }}>
                <div>
                  <h2 className="syne" style={{ fontSize:20, fontWeight:800 }}>Applicants</h2>
                  <p style={{ fontSize:12, color:'rgba(255,255,255,0.4)', marginTop:2 }}>{applications.length} total across all listings</p>
                </div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {/* Job filter */}
                  <select
                    value={selectedJob}
                    onChange={e => setSelectedJob(e.target.value)}
                    style={{ padding:'8px 12px', background:'#0f0f24', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, color:'white', fontSize:12, outline:'none', cursor:'pointer', maxWidth:200 }}
                  >
                    <option value="all">All Listings</option>
                    {jobs.filter(j => j.status === 'live').map(j => (
                      <option key={j.id} value={j.id}>{j.title}</option>
                    ))}
                  </select>
                  {/* Status filter */}
                  {['all','new','shortlisted','rejected','hired'].map(s => (
                    <button key={s} onClick={() => setAppFilter(s)} style={{ padding:'7px 13px', borderRadius:8, fontSize:11, fontWeight:700, cursor:'pointer', textTransform:'capitalize', background: appFilter===s ? `linear-gradient(135deg,${C.magenta},${C.violet})` : 'rgba(255,255,255,0.05)', border: appFilter===s ? 'none' : '1px solid rgba(255,255,255,0.1)', color:'#fff' }}>
                      {s === 'all' ? `All (${applications.length})` : `${STATUS_LABELS[s]} (${applications.filter(a=>a.status===s).length})`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Empty state */}
              {loadingApps && (
                <div style={{ textAlign:'center', padding:60, color:'rgba(255,255,255,0.3)' }}>Loading applicants…</div>
              )}
              {!loadingApps && applications.length === 0 && (
                <div style={{ textAlign:'center', padding:'60px 20px', color:'rgba(255,255,255,0.3)' }}>
                  <div style={{ fontSize:40, marginBottom:12 }}>👥</div>
                  <p style={{ fontSize:15 }}>No applications yet.</p>
                  <p style={{ fontSize:13, marginTop:6 }}>Applications will appear here once talents apply to your live listings.</p>
                </div>
              )}
              {!loadingApps && applications.length > 0 && filteredApps.length === 0 && (
                <div style={{ textAlign:'center', padding:'40px 20px', color:'rgba(255,255,255,0.3)' }}>No applicants match this filter.</div>
              )}

              {/* Applicant cards */}
              <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                {filteredApps.map(a => {
                  const statusColor = STATUS_COLORS[a.status] || C.info;
                  return (
                    <div key={a.id} className="card" style={{ padding:'clamp(16px,3vw,24px)' }}>
                      <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:`linear-gradient(90deg,${statusColor}70,transparent)` }} />
                      <div style={{ display:'flex', gap:14, alignItems:'flex-start', flexWrap:'wrap' }}>
                        {/* Avatar */}
                        <div style={{ width:50, height:50, borderRadius:'50%', background:`linear-gradient(135deg,${C.magenta},${C.violet})`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, fontWeight:800, flexShrink:0, fontFamily:"'Syne',sans-serif" }}>
                          {(a.userName || a.userEmail)?.charAt(0)?.toUpperCase()}
                        </div>

                        {/* Info */}
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:4 }}>
                            <span className="syne" style={{ fontSize:15, fontWeight:800 }}>{a.userName}</span>
                            <span style={{ fontSize:11, padding:'2px 9px', borderRadius:100, background:`${statusColor}18`, color:statusColor, fontWeight:700 }}>{STATUS_LABELS[a.status]}</span>
                          </div>
                          <div style={{ fontSize:12, color:'rgba(255,255,255,0.45)', marginBottom:6 }}>
                            {a.userEmail}
                            {a.userPhone && ` · ${a.userPhone}`}
                          </div>
                          <div style={{ fontSize:12, color:'rgba(255,255,255,0.35)', marginBottom:8 }}>
                            Applied for: <strong style={{ color:'rgba(255,255,255,0.6)' }}>{a.opportunityTitle}</strong>
                            {a.createdAt?.toDate && ` · ${a.createdAt.toDate().toLocaleDateString()}`}
                          </div>
                          {/* KYC badge */}
                          {a.kycStatus && (
                            <span style={{ fontSize:10, padding:'2px 8px', borderRadius:100, background: a.kycStatus==='approved' ? 'rgba(0,208,132,0.15)' : 'rgba(245,158,11,0.15)', color: a.kycStatus==='approved' ? C.success : C.warning, fontWeight:700 }}>
                              🪪 KYC {a.kycStatus}
                            </span>
                          )}
                        </div>

                        {/* Actions */}
                        <div style={{ display:'flex', flexDirection:'column', gap:8, alignItems:'flex-end', flexShrink:0 }}>
                          <select
                            value={a.status}
                            onChange={async e => {
                              try {
                                await updateApplicationStatus(a.id, e.target.value);
                                toast.success(`Status updated to ${e.target.value}`);
                              } catch (err) { toast.error(err.message); }
                            }}
                            style={{ padding:'7px 12px', background:'#0f0f24', border:`1px solid ${statusColor}40`, borderRadius:8, color:statusColor, fontSize:12, fontWeight:700, cursor:'pointer', outline:'none' }}
                          >
                            <option value="new">🆕 New</option>
                            <option value="shortlisted">⭐ Shortlist</option>
                            <option value="rejected">❌ Reject</option>
                            <option value="hired">🎉 Hire</option>
                          </select>
                          {a.cvUrl && (
                            <a href={a.cvUrl} target="_blank" rel="noreferrer" style={{ padding:'7px 14px', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, color:'rgba(255,255,255,0.7)', fontSize:12, fontWeight:600, cursor:'pointer', textDecoration:'none' }}>📄 View CV</a>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Comment thread modal */}
      {commentJob && (
        <CommentPanel job={commentJob} user={user} profile={profile} onClose={() => setCommentJob(null)} />
      )}

      <Footer />
    </div>
  );
}
